import { X } from "lucide-react";
import { useState, type KeyboardEvent } from "react";

import { Input } from "@/components/ui/input";

/**
 * A list of short strings, entered one at a time.
 *
 * Not a comma-separated text box: tags and SKUs contain commas often enough
 * that splitting on one silently produces two wrong values instead of one
 * right one. Enter commits, Backspace on an empty field removes the last.
 */
export function TokenInput({
  values,
  onChange,
  placeholder = "Type and press Enter",
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  function commit() {
    const value = draft.trim();
    if (!value || values.includes(value)) {
      setDraft("");
      return;
    }
    onChange([...values, value]);
    setDraft("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === "Backspace" && draft === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div className="border-input focus-within:ring-ring flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1 focus-within:ring-2">
      {values.map((value) => (
        <span
          key={value}
          className="bg-secondary text-secondary-foreground flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
        >
          {value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            onClick={() => {
              onChange(values.filter((v) => v !== value));
            }}
            className="hover:text-foreground text-muted-foreground"
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <Input
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={values.length === 0 ? placeholder : ""}
        className="h-6 min-w-24 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
      />
    </div>
  );
}
